import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ChevronLeft, Crown, Sparkles } from "lucide-react";
import PointsBreakdownCard from "@/components/PointsBreakdownCard";

export default function ParticipantDetail() {
  const { userId } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/facilitator/participants/${userId}`).then((r) => setData(r.data));
  }, [userId]);

  if (!data) return <div className="min-h-screen flex items-center justify-center text-[#8A8782]">Loading…</div>;

  const { user, journals, assessments, stats } = data;

  return (
    <div className="bg-tcpi-bg min-h-screen" data-testid="participant-detail-page">
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-16">
        <Link to="/facilitator" className="inline-flex items-center gap-1 text-sm text-[#8A8782] hover:text-[#C9A961] mb-6">
          <ChevronLeft className="w-4 h-4" /> All participants
        </Link>

        <div className="overline mb-3">Participant Profile</div>
        <h1
          className="text-4xl text-[#F7F5F0] mb-1"
          style={{ fontFamily: "Cormorant Garamond, serif" }}
        >
          {user.name}
        </h1>
        <div className="text-sm text-[#8A8782] mb-10">
          {user.email} · {user.organization || "—"}
        </div>

        {stats && (
          <>
            <div className="grid md:grid-cols-3 gap-4 mb-6" data-testid="participant-stats-row">
              <StatTile
                label="Crown Points"
                value={stats.total_points}
                suffix={`/ ${stats.total_tracks * 120}`}
                icon={Sparkles}
                testid="participant-stat-points"
              />
              <StatTile
                label="Completion"
                value={`${stats.completion_pct}%`}
                icon={Crown}
                testid="participant-stat-completion"
              />
              <StatTile
                label="Current Level"
                value={stats.level}
                icon={Crown}
                small
                testid="participant-stat-level"
              />
            </div>
            <PointsBreakdownCard
              breakdown={stats.breakdown}
              totalTracks={stats.total_tracks}
              totalPoints={stats.total_points}
              title="Crown Points Breakdown"
              className="mb-12"
            />
          </>
        )}

        {/* Journals */}
        <h2 className="text-2xl text-[#C9A961] mb-4" style={{ fontFamily: "Cormorant Garamond, serif" }}>
          Reflections ({journals.length})
        </h2>
        <div className="space-y-4 mb-12">
          {journals.length === 0 && <div className="text-[#8A8782]">No journals yet.</div>}
          {journals.map((j) => (
            <div key={j.id} className="card-bento" data-testid={`journal-${j.id}`}>
              <div className="overline text-[10px] mb-2">{j.track_slug}</div>
              <div className="text-[#F7F5F0] mb-2" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                {j.prompt}
              </div>
              <p className="text-[#D1CFC9] text-sm whitespace-pre-wrap leading-relaxed">{j.content}</p>
              {j.ai_feedback && (
                <div className="mt-4 p-3 bg-[#1A1816] border border-[#C9A961]/20 rounded">
                  <div className="overline text-[10px] mb-1">Mentor AI feedback</div>
                  <p className="text-xs text-[#D1CFC9] whitespace-pre-wrap">{j.ai_feedback}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Assessments */}
        <h2 className="text-2xl text-[#C9A961] mb-4" style={{ fontFamily: "Cormorant Garamond, serif" }}>
          Assessments ({assessments.length})
        </h2>
        <div className="space-y-4">
          {assessments.length === 0 && <div className="text-[#8A8782]">No assessments yet.</div>}
          {assessments.map((a) => (
            <div key={a.id} className="card-bento" data-testid={`assessment-${a.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="overline text-[10px]">{a.track_slug}</div>
                <div className="text-[#C9A961]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                  {a.score_pct}%
                </div>
              </div>
              <div className="text-sm text-[#D1CFC9]">
                MC: {a.mc_correct}/{a.mc_total} · T/F: {a.tf_correct}/{a.tf_total}
              </div>
              {a.scenario_response && (
                <div className="mt-3">
                  <div className="overline text-[10px] mb-1">Scenario</div>
                  <p className="text-sm text-[#D1CFC9] whitespace-pre-wrap">{a.scenario_response}</p>
                </div>
              )}
              {a.written_reflection && (
                <div className="mt-3">
                  <div className="overline text-[10px] mb-1">Reflection</div>
                  <p className="text-sm text-[#D1CFC9] whitespace-pre-wrap">{a.written_reflection}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function StatTile({ label, value, suffix, icon: Icon, small = false, testid }) {
  return (
    <div className="card-bento" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="overline text-[10px]">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-[#C9A961]" strokeWidth={1.4} />}
      </div>
      <div className="flex items-end gap-2">
        <span
          className={`${small ? "text-2xl" : "text-4xl"} text-[#C9A961]`}
          style={{ fontFamily: "Cormorant Garamond, serif" }}
        >
          {value}
        </span>
        {suffix && <span className="text-xs text-[#8A8782] mb-1">{suffix}</span>}
      </div>
    </div>
  );
}
